import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { GROUP_PLAN_PRICES, getGroupPricingRows } from '../../utils/groupPricing';
import {
  PRO50_EVENT_END_LABEL,
  PRO50_QUOTE_HONOR_DAYS,
  getPro50PricingRows,
  isPro50Active,
} from '../../utils/pro50Campaign';
import './AdminPricingPolicyPage.css';

const money = (value) => `${Number(value || 0).toLocaleString()}원`;

const historicalPolicies = [
  {
    title: '9월 10명 이상 단체 50%',
    period: '2026년 9월 (promo=group50)',
    guide: '9월 견적서는 10월 30일까지 50% 단가 그대로 결제됩니다. 10월 이벤트가와 비교해 싼 쪽이 자동 적용.',
    prices: { '3month': 30000, '6month': 55000, '12month': 100000 },
  },
  {
    title: '기존 시즌가 30%',
    period: '2026년 6월 생기부/평가계획서 시즌',
    guide: '현재 신규 안내는 중단. 기존 리드가 이 가격을 보고 들어왔으면 그대로 맥락 설명.',
    prices: { '3month': 51000, '6month': 82500, '12month': 140000 },
  },
  {
    title: '전시회/브로슈어 비밀가 50%',
    period: '전시회 QR, 소개서 QR 전용',
    guide: '전시회 등록자는 30일 무료 + 비밀가. 신규 일반 상담에는 기본 노출하지 않음.',
    prices: { '3month': 49000, '6month': 69000, '12month': 99000 },
  },
  {
    title: '방학 50% 예정',
    period: '향후 재오픈 예정',
    guide: '아직 운영 전. 현재 상담 기본 가격은 단체 할인가를 우선 안내.',
    prices: { '3month': 49000, '6month': 69000, '12month': 99000 },
  },
];

const AdminPricingPolicyPage = () => {
  const navigate = useNavigate();
  const groupRows = getGroupPricingRows().slice().reverse();
  const pro50Active = isPro50Active();
  const pro50Rows = getPro50PricingRows();

  return (
    <div className="admin-pricing-page">
      <Helmet>
        <title>가격 안내 | 왓퀴즈 Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="admin-pricing-container">
        <header className="admin-pricing-header">
          <button type="button" onClick={() => navigate('/admin')}>관리자 홈</button>
          <div>
            <p>Pricing Policy</p>
            <h1>가격 안내</h1>
            <span>
              {pro50Active
                ? `${PRO50_EVENT_END_LABEL}는 10월 이벤트가를 기본으로 안내합니다.`
                : '상담할 때는 현재 단체 할인가를 기본으로 안내합니다.'}
            </span>
          </div>
        </header>

        {pro50Active && (
          <section className="admin-pricing-hero">
            <div>
              <strong>현재 기본 정책 · {PRO50_EVENT_END_LABEL}</strong>
              <h2>10월 PRO 최대 50% (인원 제한 없음)</h2>
              <p>
                개인 결제·단체 결제·견적서 모두 1명부터 자동 적용됩니다. 다른 할인과 겹치지 않고 가장 싼 가격 하나만 적용돼요.
                10월에 발급한 견적서는 견적일로부터 {PRO50_QUOTE_HONOR_DAYS}일 안에 결제하면 같은 가격입니다.
              </p>
            </div>
            <div className="admin-pricing-note">
              <span>상담 기준</span>
              <strong>몇 개월권인가요?</strong>
              <p>
                {pro50Rows.map((row) => `${row.label} ${money(row.eventPrice)}(${row.discountPercent}%)`).join(' · ')}
              </p>
            </div>
          </section>
        )}

        <section className="admin-pricing-hero">
          <div>
            <strong>{pro50Active ? '이벤트 후 기본 정책' : '현재 기본 정책'}</strong>
            <h2>인원수별 단체 할인가</h2>
            <p>시즌 30% 특가는 내려두고, 견적서와 대량구매 결제는 구매 인원에 따라 자동 할인됩니다.</p>
          </div>
          <div className="admin-pricing-note">
            <span>상담 기준</span>
            <strong>몇 명이 구매하나요?</strong>
            <p>인원수만 확인하면 1/3/6/12개월 단가가 자동으로 정해집니다.</p>
          </div>
        </section>

        <section className="admin-pricing-card">
          <div className="admin-pricing-card-head">
            <h2>현재 단체 구매 가격표</h2>
            <p>부가세 포함, 1인 기준 단가입니다.</p>
          </div>
          <div className="admin-pricing-table-wrap">
            <table className="admin-pricing-table">
              <thead>
                <tr>
                  <th>구매 인원</th>
                  <th>추가 할인율</th>
                  {Object.entries(GROUP_PLAN_PRICES).map(([planId, plan]) => (
                    <th key={planId}>{plan.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groupRows.map((row) => (
                  <tr key={row.label}>
                    <td>{row.label}</td>
                    <td>{row.discountPercent}%</td>
                    {Object.keys(GROUP_PLAN_PRICES).map((planId) => (
                      <td key={planId}>{money(row.prices[planId])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="admin-pricing-grid">
          {historicalPolicies.map((policy) => (
            <article key={policy.title} className="admin-pricing-history">
              <span>{policy.period}</span>
              <h3>{policy.title}</h3>
              <p>{policy.guide}</p>
              <div>
                {Object.entries(policy.prices).map(([planId, price]) => (
                  <strong key={planId}>{GROUP_PLAN_PRICES[planId]?.label}: {money(price)}</strong>
                ))}
              </div>
            </article>
          ))}
        </section>

        <section className="admin-pricing-card">
          <div className="admin-pricing-card-head">
            <h2>Customer Leads에서 보는 방법</h2>
            <p>각 리드의 “안내할 가격” 컬럼을 먼저 확인합니다.</p>
          </div>
          <div className="admin-pricing-rules">
            <div>
              <strong>대량구매</strong>
              <span>이미 결제된 총액과 1인 단가를 그대로 확인합니다.</span>
            </div>
            <div>
              <strong>전시회</strong>
              <span>30일 무료 + 전시회 비밀가를 안내합니다.</span>
            </div>
            <div>
              <strong>평가계획서/생기부 시즌</strong>
              <span>과거 유입은 당시 시즌가로 맥락을 확인하고, 신규 상담은 단체 할인가로 전환합니다.</span>
            </div>
            <div>
              <strong>가격 정보 없음</strong>
              <span>Meta 양식처럼 금액 필드가 없던 리드입니다. 현재 단체 할인가를 기준으로 다시 안내합니다.</span>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminPricingPolicyPage;
